import React, { useState, useEffect } from "react";
import { Dialog } from "@material-ui/core";
import type { IModalService, IPageService } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services";
import type {
    IPartnersRepository,
    IPartnerUsersRepository,
    IRolesRepository,
    ITimeZonesRepository,
    ICountriesRepository,
    ILocalesRepository,
} from "App/Modules/Repositories";
import type { ILabel, IPartner, IPartnerUserSlim } from "App/Modules/Models";
import { Permission } from "App/Modules/Models";

import "./_partner-details.scss";
import "./_support.scss";

import { flatten, includes, map, uniqBy } from "lodash";
import type { StateService } from "@uirouter/core";
import { jsonEqual, getUserProfile } from "App/ReactComponents/Shared";
import { PartnerIntro } from "./PartnerIntro";
import { Holidays } from "./Holidays";
import { PartnerUsers } from "./PartnerUsers";
import { SupportingPartnersDialog } from "./Dialogs/SupportingPartnersDialog";
import { DataPolicyDialog } from "./Dialogs/DataPolicyDialog";
import { HolidayScheduleDialog } from "./Dialogs/HolidayScheduleDialog";
import { LabelsDialog } from "./Dialogs/LabelsDialog";
import { LocaleAndTimeDialog } from "./Dialogs/LocaleAndTimeDialog";

type InjectedProps = {
    mmPartnersRepository: IPartnersRepository;
    mmPartnerUsersRepository: IPartnerUsersRepository;
    irRolesRepository: IRolesRepository;
    mmModalService: IModalService;
    irPageService: IPageService;
    $state: StateService;
    mmTimeZonesRepository: ITimeZonesRepository;
    irCountriesRepository: ICountriesRepository;
    irLocalesRepository: ILocalesRepository;
};

export type PartnerDetailsProps = InjectedProps;

export const PartnerDetails = (props: PartnerDetailsProps) => {
    const [partner, setPartner] = useState<IPartner>();
    const [partnerUsers, setPartnerUsers] = useState<IPartnerUserSlim[]>();
    const [partnerAuthorizedPermissions, setPartnerAuthorizedPermissions] = useState<Permission[]>();
    const [hasLimitedPermissions, setHasLimitedPermissions] = useState(false);
    const [labels, setLabels] = useState<ILabel[]>();
    const [chosenLabel, setChosenLabel] = useState<ILabel>({
        id: "",
        label: "",
        description: "",
    });
    const [isLocaleAndTimeLoading, setIsLocaleAndTimeLoading] = useState(false);
    const [isDataPolicyLoading, setIsDataPolicyLoading] = useState(false);
    const [isLabelsLoading, setIsLabelsLoading] = useState(false);
    const [isSupportingPartnersDialogOpen, setIsSupportingPartnersDialogOpen] = useState(false);
    const [isLocaleAndTimeDialogOpen, setIsLocaleAndTimeDialogOpen] = useState(false);
    const [isEditLabelsDialogOpen, setIsEditLabelsDialogOpen] = useState(false);
    const [isDataPolicyDialogOpen, setIsDataPolicyDialogOpen] = useState(false);
    const [isEditHolidayScheduleDialogOpen, setIsEditHolidayScheduleDialogOpen] = useState(false);
    const { partnerId } = props.$state.params;
    const profile = getUserProfile();

    const sortAndUniqueLabels = (labelsToSort: ILabel[]): ILabel[] => {
        return uniqBy(labelsToSort, "id");
    };

    const getAllLabels = (users: IPartnerUserSlim[]): ILabel[] => {
        return sortAndUniqueLabels(flatten(map(users, (item) => item.labels)));
    };

    const getPartner = () =>
        extendWithLoadingAndFail(props.mmPartnersRepository.getPartner(partnerId)).subscribe(
            (response) => {
                if (!jsonEqual(response.data, partner)) {
                    setPartner(response.data);
                }
            },
            (error) => {
                setPartner(error);
            },
        );

    const getPartnerAuthorizedPermissions = () =>
        extendWithLoadingAndFail(props.mmPartnersRepository.getPartnerAuthorizedPermissions(partnerId)).subscribe(
            (response) => {
                if (!jsonEqual(response.data, partnerAuthorizedPermissions)) {
                    setPartnerAuthorizedPermissions(response.data);
                    setHasLimitedPermissions(!includes(response.data, Permission.PartnerSettings));
                }
            },
            (error) => {
                setPartner(error);
            },
        );

    const getPartnerUsersWithRole = () =>
        extendWithLoadingAndFail(props.mmPartnerUsersRepository.getPartnerUsersWithRole(partnerId)).subscribe(
            (response) => {
                if (!jsonEqual(response.data, partnerUsers)) {
                    setPartnerUsers(response.data);
                    setLabels(getAllLabels(response.data));
                }
            },
            (error) => {
                setPartner(error);
            },
        );

    useEffect(() => {
        getPartner();
        getPartnerAuthorizedPermissions();
        getPartnerUsersWithRole();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const editPartnerDetails = (event) => {
        event.preventDefault();
        props.mmModalService.open("irEditPartnerDetails", partner).subscribe((response) => {
            setPartner(response);
        });
    };

    const editMindcareSettings = (event) => {
        event.preventDefault();
        props.mmModalService.open("irEditMindcareSettings", partner).subscribe((response) => {
            setPartner(response);
        });
    };

    const handleLocaleAndTimeSettings = () => {
        setIsLocaleAndTimeLoading(true);
        props.mmPartnersRepository.getPartner(partnerId).subscribe(
            (response) => {
                if (!jsonEqual(response.data, partner)) {
                    setPartner(response.data);
                    setIsLocaleAndTimeLoading(false);
                }
            },
            (error) => {
                setPartner(error);
            },
        );
    };

    const handleDataPolicyChange = () => {
        setIsDataPolicyLoading(true);
        props.mmPartnersRepository.getPartner(partnerId).subscribe(
            (response) => {
                if (!jsonEqual(response.data, partner)) {
                    setPartner(response.data);
                    setIsDataPolicyLoading(false);
                }
            },
            (error) => {
                setPartner(error);
            },
        );
    };

    const handleLabelsChange = () => {
        setIsLabelsLoading(true);
        props.mmPartnerUsersRepository.getPartnerUsersWithRole(partnerId).subscribe(
            (response) => {
                if (!jsonEqual(response.data, partnerUsers)) {
                    setPartnerUsers(response.data);
                    setLabels(getAllLabels(response.data));
                    setIsLabelsLoading(false);
                }
            },
            (error) => {
                setPartner(error);
            },
        );
    };

    const handleHolidayScheduleChange = () => {
        props.mmPartnersRepository.getPartner(partnerId).subscribe(
            (response) => {
                if (!jsonEqual(response.data, partner)) {
                    setPartner(response.data);
                }
            },
            (error) => {
                setPartner(error);
            },
        );
    };

    const addPartnerUser = () => {
        props.mmModalService
            .open<IPartnerUserSlim, { partner: IPartner; withLimitedPermissions: boolean }>("irAddPartnerUser", null, {
                partner,
                withLimitedPermissions: hasLimitedPermissions,
            })
            .subscribe(
                (response) => {
                    const result = [...partnerUsers, response];
                    setPartnerUsers(result);
                    setLabels(getAllLabels(result));
                },
                (reason) => reason,
            );
    };

    const handleSupportingPartnersDialogClose = () => {
        setIsSupportingPartnersDialogOpen(false);
    };

    const handleLocaleAndTimeDialogClose = () => {
        setIsLocaleAndTimeDialogOpen(false);
    };

    const handleDataPolicyDialogClose = () => {
        setIsDataPolicyDialogOpen(false);
    };

    const handleEditLabelsDialogClose = () => {
        setIsEditLabelsDialogOpen(false);
    };

    const handleEditHolidayScheduleDialogClose = () => {
        setIsEditHolidayScheduleDialogOpen(false);
    };

    const isLoading = () => !(partner && partnerUsers && partnerAuthorizedPermissions);

    return (
        <div className="partner-details">
            <div className="partner-details__section partner-details__section--info">
                <PartnerIntro
                    partner={partner}
                    profile={profile}
                    hasLimitedPermissions={hasLimitedPermissions}
                    labels={labels}
                    chosenLabel={setChosenLabel}
                    editMindcareSettings={editMindcareSettings}
                    editPartnerDetails={editPartnerDetails}
                    isLoading={isLoading()}
                    isLocaleAndTimeLoading={isLocaleAndTimeLoading}
                    isDataPolicyLoading={isDataPolicyLoading}
                    isLabelsLoading={isLabelsLoading}
                    isSupportingPartnersDialogOpen={setIsSupportingPartnersDialogOpen}
                    isLocaleAndTimeDialogOpen={setIsLocaleAndTimeDialogOpen}
                    isEditLabelsDialogOpen={setIsEditLabelsDialogOpen}
                    isDataPolicyDialogOpen={setIsDataPolicyDialogOpen}
                />
                <Holidays
                    partner={partner}
                    profile={profile}
                    hasLimitedPermissions={hasLimitedPermissions}
                    isLoading={isLoading()}
                    isEditHolidayScheduleDialogOpen={setIsEditHolidayScheduleDialogOpen}
                />
            </div>
            <div className="partner-details__section partner-details__section--users partner-details__section--large">
                <PartnerUsers
                    partnerUsers={partnerUsers}
                    profile={profile}
                    addPartnerUser={addPartnerUser}
                    isLoading={isLoading()}
                />
            </div>
            {!isLoading() ? (
                <React.Fragment>
                    <Dialog
                        onClose={handleSupportingPartnersDialogClose}
                        open={isSupportingPartnersDialogOpen}
                        fullWidth
                        maxWidth="lg"
                    >
                        <SupportingPartnersDialog
                            onClose={handleSupportingPartnersDialogClose}
                            partner={partner}
                            partnersRepository={props.mmPartnersRepository}
                            rolesRepository={props.irRolesRepository}
                            updatePartner={setPartner}
                        />
                    </Dialog>

                    <Dialog
                        onClose={handleLocaleAndTimeDialogClose}
                        open={isLocaleAndTimeDialogOpen}
                        fullWidth
                        maxWidth="sm"
                    >
                        <LocaleAndTimeDialog
                            onClose={handleLocaleAndTimeDialogClose}
                            partner={partner}
                            partnersRepository={props.mmPartnersRepository}
                            timeZonesRepository={props.mmTimeZonesRepository}
                            countriesRepository={props.irCountriesRepository}
                            localesRepository={props.irLocalesRepository}
                            onLocaleAndTimeChange={handleLocaleAndTimeSettings}
                        />
                    </Dialog>

                    <Dialog onClose={handleDataPolicyDialogClose} open={isDataPolicyDialogOpen}>
                        <DataPolicyDialog
                            onClose={handleDataPolicyDialogClose}
                            partner={partner}
                            partnersRepository={props.mmPartnersRepository}
                            onDataPolicyChange={handleDataPolicyChange}
                        />
                    </Dialog>

                    <Dialog onClose={handleEditLabelsDialogClose} open={isEditLabelsDialogOpen} fullWidth>
                        <LabelsDialog
                            onClose={handleEditLabelsDialogClose}
                            labels={labels}
                            chosenLabel={chosenLabel}
                            partnersRepository={props.mmPartnersRepository}
                            partner={partner}
                            onLabelsChange={handleLabelsChange}
                        />
                    </Dialog>

                    <Dialog
                        onClose={handleEditHolidayScheduleDialogClose}
                        open={isEditHolidayScheduleDialogOpen}
                        fullWidth
                        maxWidth="md"
                    >
                        <HolidayScheduleDialog
                            onClose={handleEditHolidayScheduleDialogClose}
                            partner={partner}
                            partnersRepository={props.mmPartnersRepository}
                            onHolidayScheduleChange={handleHolidayScheduleChange}
                        />
                    </Dialog>
                </React.Fragment>
            ) : null}
        </div>
    );
};
